import type { PaginationParams, PaginatedResponse } from '@/types/pagination';
import { BaseApiError } from '@/services/shared/BaseApiError';
import type { PaymentGuidance } from '@/types/paymentGuidance';

// Core appointment data structure (camelCase for frontend)
export interface CalendlyAppointment {
  appointmentId: string;
  eventUri: string;
  eventUuid: string;
  userCalendarId: string;
  dieticianId: string;
  patientId: string | null;
  eventName: string;
  eventStatus: 'active' | 'canceled';
  startTime: string; // ISO 8601 UTC format
  endTime: string; // ISO 8601 UTC format
  eventTypeUri: string | null;
  locationType: string | null;
  locationValue: string | null;
  patientEmail: string;
  patientName: string | null;
  patientFirstName: string | null;
  patientLastName: string | null;
  patientTimezone: string | null;
  cancelUrl: string | null;
  rescheduleUrl: string | null;
  inviteeUri: string | null;
  inviteeStatus: 'active' | 'canceled';
  rescheduled: boolean;
  noShow: Record<string, unknown> | null;
  eventCreatedAt: string | null;
  eventUpdatedAt: string | null;
  inviteeCreatedAt: string | null;
  inviteeUpdatedAt: string | null;
  dieticianCalendlyUserUri: string | null;
  dieticianCalendlyEmail: string | null;
  dieticianCalendlyName: string | null;
  createdAt: string;
  updatedAt: string | null;
  lastFetchedAt: string | null;
  /**
   * Server-resolved fee guidance (design §4.6, §4.13.1). Optional-nullable rather than required so the
   * off-path legacy mapper in services/calendlyAppointmentApi.ts (design OQ10/OQ11) stays untouched;
   * CalendlyDomain.transformAppointmentFromApi always sets it.
   */
  paymentGuidance?: PaymentGuidance | null;
}

// API response format (snake_case from backend)
export interface CalendlyAppointmentApiResponse {
  appointment_id: string;
  event_uri: string;
  event_uuid: string;
  user_calendar_id: string;
  dietician_id: string;
  patient_id: string | null;
  event_name: string;
  event_status: 'active' | 'canceled';
  start_time: string;
  end_time: string;
  event_type_uri: string | null;
  location_type: string | null;
  location_value: string | null;
  patient_email: string;
  patient_name: string | null;
  patient_first_name: string | null;
  patient_last_name: string | null;
  patient_timezone: string | null;
  cancel_url: string | null;
  reschedule_url: string | null;
  invitee_uri: string | null;
  invitee_status: 'active' | 'canceled';
  rescheduled: boolean;
  no_show: Record<string, unknown> | null;
  event_created_at: string | null;
  event_updated_at: string | null;
  invitee_created_at: string | null;
  invitee_updated_at: string | null;
  dietician_calendly_user_uri: string | null;
  dietician_calendly_email: string | null;
  dietician_calendly_name: string | null;
  created_at: string;
  updated_at: string | null;
  last_fetched_at: string | null;
  payment_guidance?: PaymentGuidance | null;
}

// Request/Response types
export interface CreateCalendlyAppointmentRequest {
  eventUri: string;
  dieticianId: string;
}

export interface CreateCalendlyAppointmentApiRequest {
  event_uri: string;
  dietician_id: string;
}

export interface LinkPatientToAppointmentRequest {
  patientId: string;
}

export interface LinkPatientToAppointmentApiRequest {
  patient_id: string;
}

export interface GetCalendlyAppointmentsParams extends PaginationParams {
  // Inherits pagination parameters: page?, perPage?, search?, sortBy?, sortOrder?
  // Future Calendly-specific filters
  status?: 'active' | 'canceled';
  startDate?: string;
  endDate?: string;
}

export interface CalendlyAppointmentsResponse extends PaginatedResponse<CalendlyAppointment> {
  // Inherits: data: CalendlyAppointment[], pagination: PaginationMeta
  // Future Calendly-specific response metadata
  totalUpcoming?: number;
  totalCompleted?: number;
}

export interface CalendlyAppointmentsApiResponse {
  appointments: CalendlyAppointmentApiResponse[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
  // Future Calendly-specific API response metadata
  total_upcoming?: number;
  total_completed?: number;
}

// Calendly-specific error codes
export type CalendlyAppointmentErrorCode =
  | 'APPOINTMENT_NOT_FOUND'
  | 'APPOINTMENT_ALREADY_EXISTS'
  | 'CALENDLY_API_ERROR'
  | 'INVALID_EVENT_URI'
  | 'DIETICIAN_NOT_FOUND'
  | 'PATIENT_NOT_FOUND'
  | 'APPOINTMENT_ALREADY_LINKED'
  | 'UNAUTHORIZED_ACCESS'
  | 'CALENDAR_NOT_CONFIGURED'
  | 'REFRESH_FAILED'
  | 'NETWORK_ERROR'
  | 'TIMEOUT'
  | 'UNKNOWN_ERROR';

// Service-specific error class
export class CalendlyAppointmentApiError extends BaseApiError {
  constructor(
    message: string,
    status?: number,
    fieldErrors?: Record<string, string>,
    code?: CalendlyAppointmentErrorCode
  ) {
    super(message, status, fieldErrors, code);
    this.name = 'CalendlyAppointmentApiError';
  }

  static fromHttpStatus(
    status: number,
    message?: string,
    fieldErrors?: Record<string, string>
  ): CalendlyAppointmentApiError {
    const errorMap: Record<number, { message: string; code: CalendlyAppointmentErrorCode }> = {
      400: { message: 'Invalid request data', code: 'INVALID_EVENT_URI' },
      401: { message: 'Authentication required', code: 'UNAUTHORIZED_ACCESS' },
      403: { message: 'Access denied', code: 'UNAUTHORIZED_ACCESS' },
      404: { message: 'Appointment not found', code: 'APPOINTMENT_NOT_FOUND' },
      409: { message: 'Appointment already exists', code: 'APPOINTMENT_ALREADY_EXISTS' },
      502: { message: 'Calendly API error', code: 'CALENDLY_API_ERROR' },
      500: { message: 'Internal server error', code: 'UNKNOWN_ERROR' },
    };

    const error = errorMap[status] || { message: 'Unknown error', code: 'UNKNOWN_ERROR' };
    return new CalendlyAppointmentApiError(
      message || error.message,
      status,
      fieldErrors,
      error.code
    );
  }
}